import type { Patch } from "./types";
import { Voice } from "./Voice";
import { midiToFreq } from "./Synth";

// Schedule one note's worth of audio. If the patch has a `xfadeTo` and the
// note is long enough (> 100 ms) we spawn TWO voices in parallel, ramping the
// first down and the second up linearly over the duration — that's the
// "drag-sound-onto-sound = crossfade" behaviour.
//
// Works against both AudioContext and OfflineAudioContext (so live playback
// and WAV export share one rule).
export function spawnNoteVoice(
  ctx: BaseAudioContext,
  patch: Patch,
  dest: AudioNode,
  when: number,
  dur: number,
  midi: number,
  gain = 1,
): void {
  const freq = midiToFreq(midi);
  const safeDur = Math.max(0.05, dur);
  if (patch.xfadeTo && safeDur > 0.1) {
    const gA = new GainNode(ctx, { gain: 1 });
    const gB = new GainNode(ctx, { gain: 0.0001 });
    gA.connect(dest);
    gB.connect(dest);
    gA.gain.setValueAtTime(1, when);
    gA.gain.linearRampToValueAtTime(0.0001, when + safeDur);
    gB.gain.setValueAtTime(0.0001, when);
    gB.gain.linearRampToValueAtTime(1, when + safeDur);
    const va = new Voice(ctx, patch, gA);
    const vb = new Voice(ctx, patch.xfadeTo, gB);
    va.start(when, freq, patch.ampEnv, gain);
    va.stop(when + safeDur);
    vb.start(when, freq, patch.xfadeTo.ampEnv, gain);
    vb.stop(when + safeDur);
    return;
  }
  const v = new Voice(ctx, patch, dest);
  v.start(when, freq, patch.ampEnv, gain);
  v.stop(when + safeDur);
}
